import { useEffect, useMemo, useState } from 'react';

import clsx from 'clsx';
import { filter } from 'lodash';

import { LabelsService } from '@/utils/services/LabelsService';

import {
    ADD_HASHTAG,
    ADD_LABEL,
    REMOVE_HASHTAG,
    REMOVE_LABEL,
    REMOVE_PERIOD_SELECTED,
    UPDATE_PERIOD_SELECTED,
    useAlbumContext
} from '@/utils/hooks/context/useAlbumContext';
import useHashtags from '@/utils/hooks/swr/useHashtags';
import useLabels from '@/utils/hooks/swr/useLabels';
import useTranslation from '@/utils/hooks/translation/useTranslation';
import useActiveSocialProfile from '@/utils/hooks/user/useActiveSocialProfile';

import { TIKTOK_BUSINESS } from '@/lib/socialProfile';

import Icon from '@/components/base/Icon';
import ActionTag from '@/components/base/Tag/ActionTag';
import DatepickerAlbums from '@/components/composite/Datepicker/DatepickerAlbums';
import SelectAlbumTags from '@/components/pages/albums/AlbumFunnelModal/components/SelectAlbumTags';
import SelectPostType from '@/components/pages/albums/AlbumFunnelModal/components/SelectPostType';

const AlbumManualFilters = () => {
    const { t } = useTranslation('content');
    const { albumInfo, dispatch } = useAlbumContext();
    const { activeSocialProfile } = useActiveSocialProfile();
    const network = activeSocialProfile?.social_network_type;
    const isStoryAlbum = Boolean(albumInfo.criteria?.album_type);
    const [hashtagsOptions, setHashtagsOptions] = useState([]);
    const { hashtags, isLoading: isLoadingHashtags } = useHashtags({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    const {
        labels: labelsOptions,
        requestParams,
        setRequestParams
    } = useLabels({
        activeSPID: activeSocialProfile?.id,
        options: {
            revalidateOnFocus: false,
            revalidateOnReconnect: false
        }
    });

    useEffect(() => {
        if (hashtags && !isLoadingHashtags && !isStoryAlbum) {
            setHashtagsOptions(
                hashtags?.map((hashtag) => {
                    return {
                        type: 'hashtag',
                        label: `#${hashtag}`,
                        value: hashtag
                    };
                })
            );
        }
        if (hashtagsOptions && isStoryAlbum) {
            setHashtagsOptions([]);
        }
    }, [hashtags, isLoadingHashtags, isStoryAlbum]);

    useEffect(() => {
        if (isStoryAlbum) {
            setRequestParams({ media_type: 'stories' });
        }
        if (!isStoryAlbum && requestParams?.media_type) {
            setRequestParams(undefined);
        }
    }, [isStoryAlbum]);

    const debouncedSearch = useMemo(
        () => LabelsService.createDebouncedSearch(setRequestParams),
        [setRequestParams]
    );

    useEffect(() => {
        return () => {
            debouncedSearch?.cancel?.();
        };
    }, [debouncedSearch]);

    const formatAlbumTags = () => {
        const selectedLabels = filter(labelsOptions, (item) =>
            albumInfo?.criteria?.labels?.includes(item?.label)
        );

        const selectedHashtags = filter(hashtagsOptions, (item) =>
            albumInfo?.criteria?.hashtags?.includes(item?.value)
        );

        const albumTagsOptions = [...(labelsOptions || []), ...(hashtagsOptions || [])];
        const selectedAlbumTags = [...selectedLabels, ...selectedHashtags];

        return { albumTagsOptions, selectedAlbumTags };
    };

    const { albumTagsOptions, selectedAlbumTags } = formatAlbumTags();

    return (
        <div className="space-y-3 px-2 pb-5">
            <div
                className={clsx('grid gap-3', {
                    'grid-cols-3': network !== TIKTOK_BUSINESS,
                    'grid-cols-2': network === TIKTOK_BUSINESS
                })}>
                {network !== TIKTOK_BUSINESS ? <SelectPostType /> : null}
                <SelectAlbumTags
                    options={albumTagsOptions}
                    placeholder={
                        <span className="flex items-center space-x-3">
                            <Icon icon="search" />
                            <span>
                                {!isStoryAlbum
                                    ? t('modal.edit_modal.filter_on_label_hashtag')
                                    : t('modal.edit_modal.filter_on_label')}
                            </span>
                        </span>
                    }
                    buttonStyle="noBorder"
                    idInput="tags-search"
                    addItem={(item) => {
                        dispatch({
                            type: item?.type === 'hashtag' ? ADD_HASHTAG : ADD_LABEL,
                            payload: item?.type === 'hashtag' ? item.value : item?.label
                        });
                    }}
                    onSearch={debouncedSearch}
                    activeActionTags={false}
                />
                <DatepickerAlbums
                    inputStyle="datepicker"
                    noBorderShadow
                    isLeftIcon
                    periodSelected={
                        albumInfo?.criteria?.started_at && albumInfo?.criteria?.ended_at
                            ? [
                                  albumInfo?.criteria?.started_at ?? '',
                                  albumInfo?.criteria?.ended_at ?? ''
                              ]
                            : ''
                    }
                    setPeriodSelected={(date) => {
                        dispatch({
                            type: UPDATE_PERIOD_SELECTED,
                            payload: date
                        });
                    }}
                    removePeriod={() => {
                        dispatch({
                            type: REMOVE_PERIOD_SELECTED
                        });
                    }}
                    placeholder={t('modal.edit_modal.filter_on_period')}
                />
            </div>
            {selectedAlbumTags?.length > 0 ? (
                <ul className="mt-3 flex flex-wrap gap-2">
                    {selectedAlbumTags.map((item, index) => {
                        return (
                            <li key={index}>
                                <ActionTag data-testid="input-action-tag">
                                    <Icon
                                        icon={item?.type === 'hashtag' ? 'hashtag' : 'labels'}
                                        className="p-1 align-bottom"
                                    />
                                    <span className="mr-1">{item.value ?? item.label}</span>
                                    <button
                                        id={item.id}
                                        aria-label="input-action-tag-delete"
                                        className="leading-none"
                                        onClick={() => {
                                            dispatch({
                                                type:
                                                    item?.type === 'hashtag'
                                                        ? REMOVE_HASHTAG
                                                        : REMOVE_LABEL,
                                                payload:
                                                    item?.type === 'hashtag'
                                                        ? item.value
                                                        : item?.label
                                            });
                                        }}>
                                        <Icon icon="close" className="align-bottom" />
                                    </button>
                                </ActionTag>
                            </li>
                        );
                    })}
                </ul>
            ) : null}
        </div>
    );
};

export default AlbumManualFilters;
